import { Bus } from '@atlas/core';
import { StorageManager } from '@atlas/core';

export const TAG_IMG = 'img';
export const TAG_IFRAME = 'iframe';

export const DEPOSIT_EVENT = 'action_deposit_identifier';
export const REGISTER_EVENT = 'action_register_identifier';

class BtagProcessor {
  get action() {
    return this.options.action;
  }

  setAction(value) {
    this.options.action = value;
    return this;
  }

  _btag = null;

  _domElement = null;

  /**
   *
   * @type URL
   * @private
   */
  _baseUrl = null;

  /**
   *
   * @returns {URL}
   */
  getBaseUrl() {
    return this._baseUrl;
  }

  /**
   *
   * @param extraParams
   * @returns {BtagProcessor}
   */
  setExtraParams(extraParams) {
    this.options.extraParams = extraParams;
    return this;
  }

  /**
   * @param name
   * @param value
   * @returns {BtagProcessor}
   */
  addExtraParam(name, value) {
    this.options.extraParams[name] = value;
    return this;
  }

  setBaseUrl(value) {
    this._baseUrl = new URL(value);
    return this;
  }

  options = {
    domTagType: TAG_IMG,
    params: new URLSearchParams(),
    before_mount: null,
    after_mount: null,
    action: null,
    extraParams: {},
  };

  constructor(options) {
    this.options = {
      ...this.options,
      ...options,
    };
  }

  addParam(name, value) {
    this._baseUrl.searchParams.append(name, value);
    return this;
  }

  getBTag() {
    return this._btag;
  }

  setBTag(value) {
    this._btag = value;
    return this;
  }

  getDomTagType() {
    return this.options.domTagType;
  }

  setDomTagType(value) {
    this.options.domTagType = value;
    return this;
  }

  getBtagUniqueID() {
    return new BtagParser(this.getBTag()).UniqueID;
  }

  getNetworkID() {
    const pattern = new RegExp(netIDPattern, 'g');
    const result = pattern.exec(this.getBTag());
    if (result !== null) {
      return result.groups.net;
    }
  }

  getNetwork() {
    // eslint-disable-next-line no-use-before-define
    return Registry.getInstance().at(this.getNetworkID());
  }

  createDomTag() {
    const net = this.getNetwork();
    if (!net) {
      return;
    }
    const element = document.createElement(this.options.domTagType);
    element.style.height = 0;
    element.style.width = 0;
    element.style.border = 'none';
    element.style.position = 'absolute';

    element.src = net.getUrl(this.getBtagUniqueID(), this.options.action, this.options.extraParams);
    this._domElement = element;
    return this;
  }

  Mount() {
    if (this._domElement === null) {
      this.createDomTag();
    }
    if (this._domElement === null) {
      return;
    }

    this.beforeMount && this.beforeMount.call(this);

    document.body.appendChild(this._domElement);

    this.afterMount && this.beforeMount.call(this);

    return this;
  }

  beforeMount(callback) {
    this.options.before_mount = callback;
    return this;
  }

  afterMount(callback) {
    this.options.after_mount = callback;
    return this;
  }

  addEventListener(eventName, callback, once = false) {
    if (once === true) {
      Bus.once(eventName, params => {
        const btag = StorageManager.getItem('btag');
        this.setBTag(btag);
        callback(this, params);
      });
    } else {
      Bus.on(eventName, params => {
        const btag = StorageManager.getItem('btag');
        this.setBTag(btag);
        callback(this, params);
      });
    }
    return this;
  }
}

const netIDPattern = 'a_(?<net>[0-9]{1,})b';

// a_9b_10c_3957_609aa189c589ce000115267f
// eslint-disable-next-line camelcase
const btag_patterns = [
  '(?<btag>[a-z0-9]{16,})',
  '^a_[0-9]{1,3}b_[0-9]{1,4}c_[0-9]{1,9}_(?<btag>[a-z0-9]+)$',
];

class Registry {
  reg = {};

  static instance = null;

  constructor() {
    this.reg = [];
  }

  static getInstance() {
    if (Registry.instance === null) {
      Registry.instance = new Registry();
    }
    return Registry.instance;
  }

  add(name, options) {
    const net = new Network(name, options);
    this.reg[net.id] = net;
    return this;
  }

  fromConfig(data) {
    if (!data) {
      return;
    }
    for (const k in data) {
      const v = data[k];
      this.add(v.name, data[k]);
    }
  }

  at(id) {
    if (!(id in this.reg)) {
      return;
    }
    return this.reg[id];
  }
}

class Network {
  get action() {
    return this._action;
  }

  set action(value) {
    this._action = value;
  }

  get url_format() {
    return this._url_format;
  }

  set url_format(value) {
    this._url_format = value;
  }

  url_format;

  get id() {
    return this._id;
  }

  set id(value) {
    this._id = value;
  }

  get name() {
    return this._name;
  }

  set name(value) {
    this._name = value;
  }

  constructor(name, options) {
    // eslint-disable-next-line no-underscore-dangle
    this._id = options.id;
    this._action = options.action;
    this._url_format = options.url_format;
    this._name = name;
  }

  getUrl(btag, action, extra = {}) {
    return stringInterpolate(this._url_format, {
      action: this._action[action],
      btag,
      ...extra,
    });
  }
}

const stringInterpolate = (str, args = {}) => {
  for (const key in args) {
    str = str.replace(new RegExp(`\\{${key}\\}`, 'gi'), args[key]);
  }
  return str;
};

// Registry.getInstance()
class BtagParser {
  constructor(btag) {
    this.btag = btag;
  }

  get UniqueID() {
    const { btag } = this;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < btag_patterns.length; i++) {
      const pattern = new RegExp(btag_patterns[i], 'g');
      const result = pattern.exec(btag);
      if (result !== null) {
        return result.groups.btag;
      }
    }
    return null;
  }
}

export default {
  setConfig: reg => Registry.getInstance().fromConfig(reg),
  Instance: options => new BtagProcessor(options),
  // eslint-disable-next-line max-len
  on: (eventName, callback, options) =>
    new BtagProcessor(options).addEventListener(eventName, callback),
  // eslint-disable-next-line max-len
  once: (eventName, callback, options) =>
    new BtagProcessor(options).addEventListener(eventName, callback, true),
};
